import type { ReactNode } from 'react';
import { Alert, AlertTitle, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ErrorSummaryItem {
  /** The invalid control's DOM id (the `id` on its `TextField`). */
  id: string;
  /** The field's visible label. */
  label: string;
  /** The translated message the field shows. */
  message: string;
}

const FOCUSABLE =
  'input:not([type="hidden"]), select, textarea, button, a[href], [role="combobox"], [tabindex]:not([tabindex="-1"])';

/** Focuses the control `id` names — or the first control inside it (a toggle group, a radio group). */
function focusField(id: string) {
  const element = document.getElementById(id);
  if (element === null) return;
  const target = element.matches(FOCUSABLE)
    ? element
    : element.querySelector<HTMLElement>(FOCUSABLE);
  (target ?? element).focus();
}

/**
 * The error summary a long form shows at its top after a failed submit (FRM-005, A11Y-010,
 * TMMAPP-0073): a count, then one button per invalid field that moves focus to it. The summary
 * itself carries `data-error-summary`, which `useErrorFocus` focuses after the submit. Every
 * string is the field's own label and message — nothing the server wrote (TMM-B3).
 */
export function ErrorSummary({
  items,
  message,
}: {
  items: readonly ErrorSummaryItem[];
  /** The form-level line ("Please check the highlighted fields.") shown under the count. */
  message?: ReactNode;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <Alert severity="error" data-error-summary="" tabIndex={-1}>
      <AlertTitle>{t('form.errorSummary', { count: items.length })}</AlertTitle>
      {message !== undefined && message !== null && <p style={{ margin: 0 }}>{message}</p>}
      <ul style={{ margin: 0, paddingInlineStart: '1.25em' }}>
        {items.map((item) => (
          <li key={item.id}>
            <Link
              component="button"
              type="button"
              variant="body2"
              // The alert's own ink (errorText on errorSoft, an audited pair), underlined to read as a link.
              color="inherit"
              underline="always"
              onClick={() => focusField(item.id)}
              sx={{ textAlign: 'start', verticalAlign: 'baseline' }}
            >
              {t('form.errorSummaryItem', { label: item.label, message: item.message })}
            </Link>
          </li>
        ))}
      </ul>
    </Alert>
  );
}
